import { apiClient } from './api-client';
import type {
  CollectionReminderRule,
  CreateCollectionReminderRuleDto,
  CustomCollection,
  CustomCollectionEntry,
  CustomCollectionFeatureConfig,
  CustomCollectionRelationDefinition,
  CreateCustomCollectionDto,
  CreateCustomCollectionRelationDefinitionDto,
  UpdateCustomCollectionDto,
  UpdateCustomCollectionFeaturesDto,
  UpdateCustomCollectionRelationshipValuesDto,
  UpdateCustomCollectionRelationDefinitionDto,
  CreateCustomCollectionEntryDto,
  UpdateCustomCollectionEntryDto,
  CustomCollectionMember,
  AddCustomCollectionMember,
  CustomCollectionFeatureKeyEnum,
  DropdownOption,
  PaginatedResponse,
  ImportPreviewRecord,
  ImportOptions,
  ImportResult,
  ExportOptions,
} from '@scrambl/shared';

export type CustomCollectionEntryWithFields = CustomCollectionEntry & Record<string, unknown>;
export type CustomCollectionWithCount = CustomCollection & { _count: { entries: number } };

// Parsed preview from POST /custom-collections/:id/import-url (server-side
// OG/schema.org parse). Nothing is created — the client makes the entry.
export interface RecipeImportPreview {
  name: string | null;
  imageUrl: string | null;
  description: string | null;
  sourceUrl: string;
}

// Helper function to clean and normalize API parameters
function cleanParams(params: Record<string, unknown>): Record<string, string> {
  const cleaned: Record<string, string> = {};

  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value) && value.length > 0) {
        cleaned[key] = value.join(',');
      } else if (typeof value === 'object') {
        cleaned[key] = JSON.stringify(value);
      } else {
        cleaned[key] = String(value);
      }
    }
  });

  return cleaned;
}

// API client for custom collections
export const customCollectionsApi = {
  // Module counts
  async getModuleCounts(): Promise<{ allCollections: number }> {
    return apiClient<{ allCollections: number }>('/custom-collections/module-counts', { method: 'GET' });
  },

  // Reminder rules (COLLECTION_REMINDERS flag — bills due dates, renewals)
  async getReminderRules(collectionId: string): Promise<CollectionReminderRule[]> {
    return apiClient<CollectionReminderRule[]>(`/custom-collections/${collectionId}/reminder-rules`, { method: 'GET' });
  },
  async createReminderRule(
    collectionId: string,
    dto: CreateCollectionReminderRuleDto,
  ): Promise<CollectionReminderRule> {
    return apiClient<CollectionReminderRule>(`/custom-collections/${collectionId}/reminder-rules`, { method: 'POST', body: dto });
  },
  async deleteReminderRule(ruleId: string): Promise<void> {
    return apiClient<void>(`/custom-collections/reminder-rules/${ruleId}`, { method: 'DELETE' });
  },

  // Collection operations
  async getCollections(params: {
    page?: number;
    limit?: number;
    search?: string;
    columnFilters?: Record<string, unknown>;
  } = {}): Promise<PaginatedResponse<CustomCollectionWithCount>> {
    const queryParams = cleanParams({
      page: params.page,
      limit: params.limit,
      search: params.search,
      columnFilters: params.columnFilters,
    });
    const queryString = new URLSearchParams(queryParams).toString();
    return apiClient<PaginatedResponse<CustomCollectionWithCount>>(
      `/custom-collections${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  },

  async getCollection(id: string): Promise<CustomCollection> {
    return apiClient<CustomCollection>(`/custom-collections/${id}`, { method: 'GET' });
  },

  async getCollectionBySlug(slug: string): Promise<CustomCollection> {
    return apiClient<CustomCollection>(`/custom-collections/slug/${slug}`, { method: 'GET' });
  },

  async createCollection(dto: CreateCustomCollectionDto): Promise<CustomCollection> {
    return apiClient<CustomCollection>('/custom-collections', {
      method: 'POST',
      body: dto,
    });
  },

  async updateCollection(id: string, dto: UpdateCustomCollectionDto): Promise<CustomCollection> {
    return apiClient<CustomCollection>(`/custom-collections/${id}`, {
      method: 'PUT',
      body: dto,
    });
  },

  async getCollectionFeatures(collectionId: string): Promise<CustomCollectionFeatureConfig> {
    return apiClient<CustomCollectionFeatureConfig>(`/custom-collections/${collectionId}/features`, {
      method: 'GET',
    });
  },

  async updateCollectionFeatures(
    collectionId: string,
    dto: UpdateCustomCollectionFeaturesDto,
  ): Promise<CustomCollectionFeatureConfig> {
    return apiClient<CustomCollectionFeatureConfig>(`/custom-collections/${collectionId}/features`, {
      method: 'PUT',
      body: dto,
    });
  },

  async getRelationships(
    collectionId: string,
    direction: 'source' | 'target' | 'both' = 'source',
  ): Promise<CustomCollectionRelationDefinition[]> {
    const params = new URLSearchParams({ direction });
    return apiClient<CustomCollectionRelationDefinition[]>(
      `/custom-collections/${collectionId}/relationships?${params.toString()}`,
      { method: 'GET' },
    );
  },

  async createRelationship(
    collectionId: string,
    dto: CreateCustomCollectionRelationDefinitionDto,
  ): Promise<CustomCollectionRelationDefinition> {
    return apiClient<CustomCollectionRelationDefinition>(`/custom-collections/${collectionId}/relationships`, {
      method: 'POST',
      body: dto,
    });
  },

  async updateRelationship(
    collectionId: string,
    relationshipId: string,
    dto: UpdateCustomCollectionRelationDefinitionDto,
  ): Promise<CustomCollectionRelationDefinition> {
    return apiClient<CustomCollectionRelationDefinition>(
      `/custom-collections/${collectionId}/relationships/${relationshipId}`,
      {
        method: 'PUT',
        body: dto,
      },
    );
  },

  async deleteRelationship(collectionId: string, relationshipId: string): Promise<void> {
    await apiClient<void>(`/custom-collections/${collectionId}/relationships/${relationshipId}`, {
      method: 'DELETE',
      skipJsonParse: true,
    });
  },

  async getCollectionMembers(collectionId: string): Promise<CustomCollectionMember[]> {
    return apiClient<CustomCollectionMember[]>(`/custom-collections/${collectionId}/members`, { method: 'GET' });
  },

  async addCollectionMember(collectionId: string, data: AddCustomCollectionMember): Promise<CustomCollectionMember> {
    return apiClient<CustomCollectionMember>(`/custom-collections/${collectionId}/members`, {
      method: 'POST',
      body: data,
    });
  },

  async removeCollectionMember(collectionId: string, memberId: string): Promise<void> {
    await apiClient<any>(`/custom-collections/${collectionId}/members/${memberId}`, {
      method: 'DELETE',
      skipJsonParse: true,
    });
  },

  async deleteCollection(id: string): Promise<void> {
    await apiClient<any>(`/custom-collections/${id}`, { method: 'DELETE', skipJsonParse: true });
  },

  async bulkDeleteCollections(ids: string[]): Promise<{ deletedCount: number }> {
    return apiClient<{ deletedCount: number }>('/custom-collections/bulk-delete', {
      method: 'POST',
      body: { ids },
    });
  },

  // Entry operations
  async getEntries(
    collectionId: string,
    params: {
      page?: number;
      limit?: number;
      search?: string;
      columnFilters?: Record<string, unknown>;
    } = {}
  ): Promise<PaginatedResponse<CustomCollectionEntryWithFields>> {
    const queryParams = cleanParams({
      page: params.page,
      limit: params.limit,
      search: params.search,
      columnFilters: params.columnFilters,
    });
    const queryString = new URLSearchParams(queryParams).toString();
    return apiClient<PaginatedResponse<CustomCollectionEntryWithFields>>(
      `/custom-collections/${collectionId}/entries${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  },

  async getEntry(collectionId: string, entryId: string): Promise<CustomCollectionEntryWithFields> {
    return apiClient<CustomCollectionEntryWithFields>(
      `/custom-collections/${collectionId}/entries/${entryId}`,
      { method: 'GET' }
    );
  },

  async searchEntries(collectionId: string, q: string, limit?: number): Promise<CustomCollectionEntry[]> {
    const params = new URLSearchParams({ q });
    if (limit) params.append('limit', String(limit));
    return apiClient<CustomCollectionEntry[]>(`/custom-collections/${collectionId}/entries/search?${params.toString()}`, { method: 'GET' });
  },

  async createEntry(collectionId: string, dto: CreateCustomCollectionEntryDto): Promise<CustomCollectionEntryWithFields> {
    return apiClient<CustomCollectionEntryWithFields>(`/custom-collections/${collectionId}/entries`, {
      method: 'POST',
      body: dto,
    });
  },

  // Recipe URL import (meal planner) — fetches a preview of a pasted recipe
  // link; pair with createEntry to actually save it to the collection.
  async importFromUrl(collectionId: string, url: string): Promise<RecipeImportPreview> {
    return apiClient<RecipeImportPreview>(`/custom-collections/${collectionId}/import-url`, {
      method: 'POST',
      body: { url },
    });
  },

  async previewImportEntries(
    collectionId: string,
    records: Record<string, unknown>[],
    options: ImportOptions
  ): Promise<ImportPreviewRecord[]> {
    return apiClient<ImportPreviewRecord[]>(`/custom-collections/${collectionId}/entries/import/preview`, {
      method: 'POST',
      body: { records, options },
    });
  },

  async importEntries(
    collectionId: string,
    records: ImportPreviewRecord[],
    options: ImportOptions
  ): Promise<ImportResult> {
    return apiClient<ImportResult>(`/custom-collections/${collectionId}/entries/import`, {
      method: 'POST',
      body: { records, options },
    });
  },

  async exportEntries(
    collectionId: string,
    options: ExportOptions
  ): Promise<CustomCollectionEntryWithFields[]> {
    return apiClient<CustomCollectionEntryWithFields[]>(`/custom-collections/${collectionId}/entries/export`, {
      method: 'POST',
      body: options,
    });
  },

  async updateEntry(collectionId: string, entryId: string, dto: UpdateCustomCollectionEntryDto): Promise<CustomCollectionEntryWithFields> {
    return apiClient<CustomCollectionEntryWithFields>(`/custom-collections/${collectionId}/entries/${entryId}`, {
      method: 'PUT',
      body: dto,
    });
  },

  async getEntryRelationshipValues(
    collectionId: string,
    entryId: string,
    relationshipId: string,
  ): Promise<Array<{ type: 'CUSTOM_COLLECTION'; id: string; name: string }>> {
    return apiClient(`/custom-collections/${collectionId}/entries/${entryId}/relationships/${relationshipId}`, {
      method: 'GET',
    });
  },

  async updateEntryRelationshipValues(
    collectionId: string,
    entryId: string,
    relationshipId: string,
    dto: UpdateCustomCollectionRelationshipValuesDto,
  ): Promise<Array<{ type: 'CUSTOM_COLLECTION'; id: string; name: string }>> {
    return apiClient(`/custom-collections/${collectionId}/entries/${entryId}/relationships/${relationshipId}`, {
      method: 'PUT',
      body: dto,
    });
  },

  async getInverseRelationshipEntries(
    collectionId: string,
    entryId: string,
    relationshipId: string,
  ): Promise<{
    relationship: CustomCollectionRelationDefinition;
    entries: Array<{ id: string; name: string }>;
  }> {
    return apiClient(
      `/custom-collections/${collectionId}/entries/${entryId}/inverse-relationships/${relationshipId}`,
      {
        method: 'GET',
      },
    );
  },

  // Removed: getEntryModuleItems — was the bespoke "stripped-down linked items"
  // endpoint behind the old chip-list tabs. Collection entry tabs now use the
  // full-featured module tables (projects / contacts / communications) with
  // polymorphic moduleType+moduleId filters against the normal list endpoints.

  async deleteEntry(collectionId: string, entryId: string): Promise<void> {
    await apiClient<any>(`/custom-collections/${collectionId}/entries/${entryId}`, { method: 'DELETE', skipJsonParse: true });
  },

  async bulkDeleteEntries(collectionId: string, entryIds: string[]): Promise<{ deletedCount: number }> {
    return apiClient<{ deletedCount: number }>(`/custom-collections/${collectionId}/entries/bulk-delete`, {
      method: 'POST',
      body: { ids: entryIds },
    });
  },

  // Utility operations
  async getDropdownOptions(collectionId: string): Promise<DropdownOption[]> {
    return apiClient<DropdownOption[]>(`/custom-collections/${collectionId}/dropdown-options`, { method: 'GET' });
  },
};
